/**
 * Erro do navegador vai para o servidor, e dali para o painel de problemas (ADR 0022).
 *
 * O que sai é tipo, mensagem, pilha e a rota com os ids trocados por marcador. Nunca o
 * conteúdo da tela: a mensagem da paciente não está no erro, e não entra por ele.
 */
import { PUBLIC_ROUTES, type AmbienteInfo, type ClientErrorReport } from '@zenda/api-contract';
import { ApiRequestError, getToken } from './api';

let ambiente: AmbienteInfo | null = null;
const jaRelatados = new Set<string>();

export async function carregarAmbiente(): Promise<AmbienteInfo | null> {
  if (ambiente !== null) return ambiente;
  try {
    const r = await fetch(PUBLIC_ROUTES.ambiente, { headers: { Accept: 'application/json' } });
    if (r.ok) ambiente = (await r.json()) as AmbienteInfo;
  } catch {
    return null;
  }
  return ambiente;
}

const rotaAtual = (): string =>
  window.location.pathname
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
    .replace(/\/conectar\/[^/]+/, '/conectar/:token');

export function relatarErro(
  kind: ClientErrorReport['kind'],
  erro: unknown,
  requestId?: string,
): void {
  const e =
    erro instanceof Error ? erro : new Error(typeof erro === 'string' ? erro : 'erro sem mensagem');
  const chave = `${kind}|${e.message}|${rotaAtual()}`;
  if (jaRelatados.has(chave) || jaRelatados.size > 50) return;
  jaRelatados.add(chave);

  const corpo: ClientErrorReport = {
    kind,
    message: `${e.name}: ${e.message}`.slice(0, 300),
    ...(e.stack === undefined ? {} : { stack: e.stack.slice(0, 4000) }),
    route: rotaAtual(),
    ...(ambiente === null ? {} : { release: ambiente.release }),
    ...(requestId === undefined ? {} : { requestId }),
  };
  const token = getToken();
  void fetch(PUBLIC_ROUTES.erroCliente, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(corpo),
    keepalive: true,
  }).catch(() => undefined);
}

/**
 * Falha que o React Query segura não vira `unhandledrejection`: ela ia para a tela e
 * morria ali. Sobe só o que o servidor não viu. Sem rede, a requisição nunca chegou; e
 * erro que não é de API (resposta que não era JSON, bug num `select`) nem passou por
 * ele. 4xx e 5xx já estão na trilha da requisição, do lado de lá.
 */
export function relatarFalhaDeConsulta(erro: unknown): void {
  if (erro instanceof ApiRequestError) {
    if (erro.code === 'network_error') relatarErro('api', erro, erro.requestId ?? undefined);
    return;
  }
  relatarErro('error', erro);
}

export function instalarRastreioDoNavegador(): void {
  void carregarAmbiente();
  window.addEventListener('error', (ev) => {
    relatarErro('error', ev.error ?? ev.message);
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const motivo: unknown = ev.reason;
    // Falha de API já foi registrada pelo servidor, com a trilha inteira.
    if (motivo instanceof Error && motivo.name === 'ApiRequestError') return;
    relatarErro('unhandledrejection', motivo);
  });
}
